import React from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';

// deviceStatuses format: { PHONE: 'poor', DESKTOP: 'good', TABLET: 'needs-improvement', ALL: 'poor' }
const DeviceToggle = ({ value, onChange, showTablet = true, pageSpeedMode = false, className = '', deviceStatuses = {} }) => {
  const { t } = useLanguage();
  
  const DEVICE_OPTIONS = [
    { value: 'ALL', label: t('ui.devices.all'), icon: 'Layers' },
    { value: 'PHONE', label: t('ui.devices.mobile'), icon: 'Phone' },
    { value: 'DESKTOP', label: t('ui.devices.desktop'), icon: 'Monitor' },
    { value: 'TABLET', label: t('ui.devices.tablet'), icon: 'Tablet' }
  ];

  const PAGESPEED_OPTIONS = [
    { value: 'PHONE', label: t('ui.devices.mobile'), icon: 'Phone' },
    { value: 'DESKTOP', label: t('ui.devices.desktop'), icon: 'Monitor' }
  ];

  let options;
  if (pageSpeedMode) {
    options = PAGESPEED_OPTIONS;
  } else {
    options = showTablet 
      ? DEVICE_OPTIONS 
      : DEVICE_OPTIONS.filter(o => o.value !== 'TABLET');
  }

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={14} /> : null;
  };

  const getActiveColor = (optValue) => {
    if (value !== optValue) return '';
    switch (optValue) {
      case 'ALL': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'PHONE': return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
      case 'DESKTOP': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'TABLET': return 'bg-amber-500/20 text-amber-400 border-amber-500/50';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
    }
  };

  // Get status indicator for device (only show for non-selected, non-good devices)
  const getStatusIndicator = (optValue) => {
    if (value === optValue) return null; // Don't show on selected device
    const status = deviceStatuses[optValue];
    if (!status || status === 'good') return null;
    
    const indicatorClass = status === 'poor' 
      ? 'bg-red-500 shadow-red-500/50' 
      : 'bg-amber-500 shadow-amber-500/50';
    
    return (
      <span 
        className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${indicatorClass} shadow-lg animate-pulse`}
        title={status === 'poor' ? t('ui.devices.poorMetrics') : t('ui.devices.needsImprovement')}
      />
    );
  };

  return (
    <div className={`inline-flex flex-wrap sm:flex-nowrap items-center gap-1 sm:gap-1 ${className}`}>
      {options.map(option => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`relative flex items-center gap-1 px-2 sm:px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all border whitespace-nowrap ${
            value === option.value
              ? getActiveColor(option.value)
              : 'bg-transparent text-slate-400 border-transparent hover:bg-slate-800 hover:text-slate-300'
          }`}
        >
          {getIcon(option.icon)}
          <span>{option.label}</span>
          {getStatusIndicator(option.value)}
        </button>
      ))}
    </div>
  );
};

export default DeviceToggle;
